/**
 * fetchMetrics.js
 * ---------------------------------------------------------------------
 * Fetches metric data from the backend API for the current map view
 * and updates the component’s state accordingly.
 *
 * It supports two modes:
 *   1. "explore" → fetches group.keys for visualising survey data
 *   2. "filter"  → fetches TargetVariable1 + TargetVariable2 sets
 *
 * ---------------------------------------------------------------------
 * 🧭 OVERVIEW
 *
 * 1️⃣ Determines which variables to request based on:
 *    - The active group (via getActiveGroup)
 *    - The current ActionMode ("explore" or "filter")
 *
 * 2️⃣ Builds and sends a POST request to `/api/[level]_data_arrow/`
 *     - Uses Apache Arrow format for efficient transfer
 *     - Applies active filters and theme
 *
 * 3️⃣ Parses Arrow IPC → JS objects (via tableFromIPC & rowsFromTable)
 *
 * 4️⃣ Updates component state with new `metricRows`
 *     - Handles constituency & ward data consistently
 *     - Sets loading flags, preserves previous data if needed
 *
 * 5️⃣ Aborts old requests if new ones start (using AbortController)
 *
 * 6️⃣ Throttles repeat requests made within 700ms
 *
 * 7️⃣ On completion, triggers sidebar refresh via onFeatureSelect()
 *
 * ---------------------------------------------------------------------
 * @param {Object} params
 * @param {string} params.level - Geographic level ("constituency", "ward", "local_authority")
 * @param {string} [params.constituencyCode] - Optional parent/specific area code
 * @param {Object} params.props - React props (contains ActionMode, selectedSection, etc.)
 * @param {Function} params.setState - Function to update component state
 * @param {Function} params.getActiveGroup - Returns currently active variable group
 * @param {Function} params.getFilters - Returns current filter configuration
 * @param {Function} params.requestKey - Generates unique cache key per request
 * @param {Function} params.getAggregatesForSidebar - Recomputes sidebar summary
 * @param {Object} params._internals - Holds internal request tracking vars (inflight, lastKey, lastAt)
 * @param {Function} params.tableFromIPC - Converts Arrow IPC → Table
 * @param {Function} params.rowsFromTable - Converts Arrow Table → JS rows
 */
import { schemaRequestFields } from "../schemaTypes";
import { isTopLevel } from "../geoLevels";

export async function fetchMetrics({
  level,
  constituencyCode,
  props,
  setState,
  getActiveGroup,
  getFilters,
  requestKey,
  getAggregatesForSidebar,
  _internals,
  tableFromIPC,
  rowsFromTable,
}) {
  const { ActionMode, selectedSection, onFeatureSelect } = props;

  // --- 1️⃣ Determine active group and mode ---
  const group = getActiveGroup();
  const isExplore = ActionMode === "explore";
  const targetSubVar1 = props?.ActionTargetSubVariables?.TargetSubVariable1;

  const resolveFilterKeys = (targetVar, _selectedSubVar) => {
    if (!targetVar) return [];
    if (targetVar.type !== "likert_grid") return targetVar.keys || [];
    // Include all sector sub-variable keys so target-area drilldown ordering
    // can rank sectors by the selected area.
    const subVarIds = targetVar.subVariables || [];
    const allKeys = subVarIds.flatMap((svId) => props.groupsById?.[svId]?.keys || []);
    return Array.from(new Set(allKeys));
  };

  const target1Keys = resolveFilterKeys(group?.TargetVariable1, targetSubVar1);
  const target2Keys = group?.TargetVariable2?.keys || [];

  // Guard: if no valid group, clear metrics
  if (
    !group ||
    (isExplore && !group.keys?.length) ||
    (!isExplore && !(target1Keys.length || target2Keys.length))
  ) {
    setState({ metricRows: null });
    return;
  }

  // --- 2️⃣ Build request body ---
  const theme = selectedSection || "pol";
  const filters = getFilters();
  const reqKey = requestKey(level, constituencyCode);

  // --- 3️⃣ Throttle duplicate requests ---
  const now = Date.now();
  if (_internals.lastKey === reqKey && now - _internals.lastAt < 700) return;
  _internals.lastKey = reqKey;
  _internals.lastAt = now;

  // --- 4️⃣ Abort previous inflight request if any ---
  if (_internals.inflight) _internals.inflight.abort();
  _internals.inflight = new AbortController();

  // --- 5️⃣ Determine which variable keys to fetch ---
const shouldAddTurnout =
    (isExplore && group?.label === "Vote intention") ||
    (!isExplore &&
      ["targetvariable1", "targetvariable2"].some(
        (v) => group?.[v] === "vote_intention"
      ));

    const groupKeys = isExplore
  ? [
      ...(group?.keys || []),
      ...(shouldAddTurnout ? ["pol:turnout:turnout"] : []),
    ]
  : [
      ...target1Keys,
      ...target2Keys,
      ...(shouldAddTurnout ? ["pol:turnout:turnout"] : []),
    ];
  const requestSchema = isExplore
    ? group?.schema
    : (group?.TargetVariable1?.schema || group?.TargetVariable2?.schema || group?.schema);
  const body = {
    areaCodes: constituencyCode ? [constituencyCode] : [],
    theme,
    groupKeys,
    filters,
    ...schemaRequestFields(requestSchema),
    normalise_over_options: isExplore
      ? (group?.normalise_over_options || false)
      : (group?.TargetVariable1?.normalise_over_options || group?.TargetVariable2?.normalise_over_options || false),
  };

  // --- 6️⃣ Trigger loading state ---
  setState({ loading: true });

  // --- 7️⃣ Fetch data ---
  try {
    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: _internals.inflight.signal,
    });

    if (!res.ok) throw new Error(`metrics fetch failed ${res.status}`);

    // --- 8️⃣ Parse Arrow response into JS rows ---
    const buf = await res.arrayBuffer();
    const table = tableFromIPC(buf);
    const rows = rowsFromTable(table);

    // --- 9️⃣ Update state with new data ---
    // metricRowsConstituency holds the PARENT area's rows — the tier the
    // sidebar compares wards against. Wards drill down from local authorities
    // as well as constituencies, so it is filled for either top level; the
    // name predates local authorities.
    setState(() => ({
      metricRows: rows,
      loading: false,
      ...(isTopLevel(level) && { metricRowsConstituency: rows }),
    }));
  } catch (e) {
    // Ignore aborts; log other errors
    if (e.name !== "AbortError") console.error("metrics error:", e);
    setState({ metricRows: null, loading: false });
  } finally {
    // --- 🔟 Cleanup inflight flag ---
    _internals.inflight = null;

    // // --- 11️⃣ Refresh sidebar summary and selection ---
    // const data = getAggregatesForSidebar();
    // onFeatureSelect?.({
    //   feature: props.selectedFeature,
    //   data,
    //   constCode: props.selectedConstituencyCode,
    //   constName: props.selectedConstituencyName || null,
    // });
  }
}
